import React from 'react';
import LegalLayout, { TocSection } from '../../components/legal/LegalLayout';
import { useLanguage } from '../../context/LanguageContext';

const TermsOfUse: React.FC = () => {
  const { language } = useLanguage();

  if (language === 'ar') {
    const arSections: TocSection[] = [
      { id: 'kabul-sartlari', title: '1. قبول الشروط والأحكام العامة' },
      { id: 'fikri-mulkiyet', title: '2. حقوق الملكية الفكرية والتأليف' },
      { id: 'kullanim-kurallari', title: '3. الاستخدام المقبول والمحظورات' },
      { id: 'dis-baglantilar', title: '4. الروابط الخارجية وإخلاء المسؤولية' },
      { id: 'hizmet-degisiklikleri', title: '5. انقطاع الخدمة وحدود المسؤولية' },
      { id: 'degisiklik-hakki', title: '6. حق تعديل وتحديث الشروط' },
      { id: 'uygulanacak-hukuk', title: '7. القانون واجب التطبيق والتواصل' },
    ];

    return (
      <LegalLayout
        title="شروط الاستخدام"
        subtitle="شروط وأحكام استخدام الموقع الإلكتروني"
        lastUpdated="01 سبتمبر 2026"
        activeTab="terms"
        sections={arSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              §
            </span>
            <span>الشروط العامة للاتفاقية</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            من خلال الوصول إلى هذا الموقع (tevgerajinenazad.com) واستخدامه، فإنك تقر بأنك قد قرأت وفهمت شروط الاستخدام الموضحة أدناه، وأنك توافق عليها بالكامل ودون أي قيود.
          </p>
        </div>

        {/* Section 1 */}
        <section id="kabul-sartlari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">01.</span>
            قبول الشروط والأحكام العامة
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            تتم إدارة هذا الموقع من قبل حركة المرأة الحرة (TJA) لأغراض التوعية العامة، والدفاع عن الحقوق، والنشر الرقمي. يلتزم جميع الزوار الذين يستفيدون من الخدمات المعروضة على الموقع بالامتثال لهذه الشروط.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            إذا كنت لا توافق على هذه الشروط، يُرجى عدم الاستمرار في استخدام موقعنا.
          </p>
        </section>

        {/* Section 2 */}
        <section id="fikri-mulkiyet" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">02.</span>
            حقوق الملكية الفكرية والتأليف
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            جميع المحتويات المتوفرة على الموقع (بما في ذلك النصوص، التقارير، البيانات الصحفية، المقالات، الرسومات، الشعارات، الصور، المقاطع الصوتية/المرئية، وعناصر التصميم) هي ملك لـ TJA ومحمية بموجب التشريعات الوطنية والدولية لحقوق الملكية الفكرية.
          </p>
          <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-3">
            <h4 className="font-bold text-brand-purple-900 text-sm">شروط استخدام المحتوى:</h4>
            <ul className="space-y-2 text-sm text-gray-600 font-light list-disc list-inside">
              <li>يجوز اقتباس التقارير والنصوص لأغراض غير تجارية بشرط الإشارة الصريحة إلى المصدر ووضع رابط مباشر.</li>
              <li>يُحظر تماماً استغلال محتويات الموقع كمنتجات أو منشورات تجارية دون إذن كتابي مسبق.</li>
              <li>لا يجوز نسخ أو تعديل الشعارات والتصميمات الرسومية الأصلية بدون موافقة خطية صريحة من TJA.</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section id="kullanim-kurallari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">03.</span>
            الاستخدام المقبول والمحظورات
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            عند استخدام موقعنا، يُشترط الالتزام بالقواعد والقوانين السارية. وتشمل الأفعال المحظورة ما يلي:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="ml-2">🚫</span> الكشط التلقائي واستخراج البيانات
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                يُحظر استخدام البرمجيات الآلية (Bots) أو العناكب (Spiders) أو أي أدوات تلقائية للوصول إلى قاعدة بيانات الموقع واستخراج البيانات بشكل جماعي (Scraping).
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="ml-2">🚫</span> الهجمات السيبرانية والبرمجيات الخبيثة
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                يُحظر رفع أو نشر الفيروسات أو ملفات التروجان أو أي برمجيات ضارة، أو شن هجمات حجب الخدمة (DDoS) التي تسبب عبئاً على الخوادم.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="ml-2">🚫</span> محاولات الوصول غير المصرح به
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                يُعد الوصول غير المصرح به إلى لوحة الإدارة أو قواعد البيانات أو البنية التحتية للخوادم جريمة يُعاقب عليها القانون.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="ml-2">🚫</span> الاستخدام المضلل للنماذج
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                يُحظر إدخال بيانات شخصية مزيفة أو مضللة أو تعود لهويات أشخاص آخرين في نماذج الاتصال.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="dis-baglantilar" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">04.</span>
            الروابط الخارجية وإخلاء المسؤولية
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            قد يحتوي موقعنا على روابط لمواقع خارجية لأغراض إعلامية وتوثيقية. لا تملك TJA أي سيطرة أو مسؤولية على محتوى أو سياسات الخصوصية أو ممارسات تلك المواقع الخارجية.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            عند النقر على الروابط الخارجية، تخضع لشروط الاستخدام الخاصة بتلك المواقع الخارجية المعنية.
          </p>
        </section>

        {/* Section 5 */}
        <section id="hizmet-degisiklikleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">05.</span>
            انقطاع الخدمة وحدود المسؤولية
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            لا تضمن TJA أن الموقع سيعمل بشكل مستمر ودون أي أخطاء. لا تتحمل TJA المسؤولية عن أي انقطاعات مؤقتة ناجمة عن أعمال الصيانة أو التحديثات أو الأعطال الفنية في الخوادم.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            المحتويات والأخبار والتقارير المنشورة هي ذات طابع إعلامي وتوعوي عام ولا تشكل استشارة قانونية.
          </p>
        </section>

        {/* Section 6 */}
        <section id="degisiklik-hakki" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">06.</span>
            حق تعديل وتحديث الشروط
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            تحتفظ TJA بالحق في تعديل أو تحديث شروط الاستخدام هذه في أي وقت ومن جانب واحد ودون إشعار مسبق.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            تسري الشروط المحدثة اعتباراً من تاريخ نشرها على الموقع. استمرارك في استخدام الموقع يعني قبولك للتعديلات الجديدة.
          </p>
        </section>

        {/* Section 7 */}
        <section id="uygulanacak-hukuk" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">07.</span>
            القانون واجب التطبيق والتواصل
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            تخضع شروط الاستخدام هذه لقوانين الجمهورية التركية. وتختص الجهات القضائية المختصة بالفصل في أي نزاع قد ينشأ عن تطبيق هذه الشروط.
          </p>
          <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-brand-purple-900 text-sm">لأي استفسارات:</h4>
              <p className="text-xs text-gray-500 font-light">تواصلوا معنا حول أي أسئلة تخص شروط الاستخدام.</p>
            </div>
            <a
              href="mailto:tja.bilgi@gmail.com"
              className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
            >
              tja.bilgi@gmail.com
            </a>
          </div>
        </section>
      </LegalLayout>
    );
  }

  if (language === 'en') {
    const enSections: TocSection[] = [
      { id: 'kabul-sartlari', title: '1. Acceptance of Terms and General Provisions' },
      { id: 'fikri-mulkiyet', title: '2. Intellectual Property and Copyright' },
      { id: 'kullanim-kurallari', title: '3. Acceptable Use and Prohibitions' },
      { id: 'dis-baglantilar', title: '4. Third-Party Links and Disclaimer' },
      { id: 'hizmet-degisiklikleri', title: '5. Service Interruptions and Limitation of Liability' },
      { id: 'degisiklik-hakki', title: '6. Right to Amend and Update Terms' },
      { id: 'uygulanacak-hukuk', title: '7. Applicable Law and Contact' },
    ];

    return (
      <LegalLayout
        title="Terms of Use"
        subtitle="Website Terms and Conditions of Service"
        lastUpdated="September 01, 2026"
        activeTab="terms"
        sections={enSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              §
            </span>
            <span>General Agreement Terms</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            By accessing and using this website (tevgerajinenazad.com), you acknowledge that you have read, understood, and agreed without limitation to be bound by the Terms of Use set forth below.
          </p>
        </div>

        {/* Section 1 */}
        <section id="kabul-sartlari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
            Acceptance of Terms and General Provisions
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            This website is operated by Tevgera Jinên Azad (TJA) for informational purposes, public awareness, human rights advocacy, and digital publishing. All visitors who benefit from the services offered on the site are obliged to comply with these terms.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            If you do not agree to these terms, please do not continue using our website.
          </p>
        </section>

        {/* Section 2 */}
        <section id="fikri-mulkiyet" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
            Intellectual Property and Copyright
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            All content on the site (including texts, reports, press releases, articles, graphics, emblems, photographs, audio/video files, and design elements) belongs to TJA and is protected under national and international copyright legislation.
          </p>
          <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-3">
            <h4 className="font-bold text-brand-purple-900 text-sm">Content Usage Conditions:</h4>
            <ul className="space-y-2 text-sm text-gray-600 font-light list-disc list-inside">
              <li>Reports and articles on the site may be cited for non-commercial purposes provided that proper attribution and a direct link are provided.</li>
              <li>Compiling or publishing website content as a commercial product or publication without prior written consent is strictly prohibited.</li>
              <li>Original logos and graphic elements may not be copied or modified without express written permission from TJA.</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section id="kullanim-kurallari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
            Acceptable Use and Prohibitions
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            When using our website, you must comply with applicable rules and laws. Prohibited actions include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Automated Scraping & Data Extraction
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Accessing the site's database using bots, crawlers, spiders, or automated tools to scrape bulk data is strictly forbidden.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Cyber Attacks and Malicious Code
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Uploading viruses, trojans, worms, or other malicious software, or launching DDoS attacks that impose excessive load on servers is prohibited.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Unauthorized Access Attempts
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Attempting unauthorized access to administrative panels, databases, or server infrastructure constitutes a criminal offense.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Misleading Form Submissions
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Submitting fraudulent, deceptive, or unauthorized personal data belonging to third parties through contact forms is prohibited.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="dis-baglantilar" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
            Third-Party Links and Disclaimer
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Our site may contain links to third-party websites for informational convenience. TJA has no control over and assumes no responsibility for the content, privacy policies, or practices of external websites.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            When you click on third-party links, you become subject to the terms of use and policies of those respective sites.
          </p>
        </section>

        {/* Section 5 */}
        <section id="hizmet-degisiklikleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
            Service Interruptions and Limitation of Liability
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            TJA does not guarantee that the website will operate uninterrupted or error-free. TJA cannot be held liable for temporary downtime resulting from maintenance, updates, or server malfunctions.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            News and reports featured on the site are for general informational purposes only and do not constitute professional legal advice.
          </p>
        </section>

        {/* Section 6 */}
        <section id="degisiklik-hakki" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">06.</span>
            Right to Amend and Update Terms
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            TJA reserves the right to unilaterally amend or update these Terms of Use at any time without prior notice.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Updated terms take effect immediately upon being posted on the website. Continued use of our website signifies your acceptance of the revised terms.
          </p>
        </section>

        {/* Section 7 */}
        <section id="uygulanacak-hukuk" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">07.</span>
            Applicable Law and Contact
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            These Terms of Use are governed by the laws of the Republic of Turkey. Competent legal authorities have jurisdiction over any disputes arising from the application of these terms.
          </p>
          <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-brand-purple-900 text-sm">For Any Inquiries:</h4>
              <p className="text-xs text-gray-500 font-light">Reach out to us with any questions regarding these Terms of Use.</p>
            </div>
            <a
              href="mailto:tja.bilgi@gmail.com"
              className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
            >
              tja.bilgi@gmail.com
            </a>
          </div>
        </section>
      </LegalLayout>
    );
  }

  if (language === 'krm') {
    const krmSections: TocSection[] = [
      { id: 'kabul-sartlari', title: '1. Pejirandina Mercan û Xalên Giştî' },
      { id: 'fikri-mulkiyet', title: '2. Mafên Mulkiyeta Rewşenbîrî û Çapê' },
      { id: 'kullanim-kurallari', title: '3. Bikaranîna Qebûlkirî û Qedexe' },
      { id: 'dis-baglantilar', title: '4. Girêdanên Aliyên Sêyem û Berpirsiyarî' },
      { id: 'hizmet-degisiklikleri', title: '5. Qutbûna Xizmetê û Sînorê Berpirsiyariyê' },
      { id: 'degisiklik-hakki', title: '6. Mafê Guhertin û Nûvekirina Mercan' },
      { id: 'uygulanacak-hukuk', title: '7. Qanûna Bicîhbûyî û Têkilî' },
    ];

    return (
      <LegalLayout
        title="Mercên Bikaranînê"
        subtitle="Merc û Şertên Bikaranîna Malperê"
        lastUpdated="01 Êlûn 2026"
        activeTab="terms"
        sections={krmSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              §
            </span>
            <span>Mercên Giştî yên Peymanê</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            Bi gihîştin û bikaranîna vê malperê (tevgerajinenazad.com), hûn qebûl dikin ku we Mercên Bikaranînê yên li jêr bi tevahî xwendiye, fêm kiriye û bê sînor pejirandiye.
          </p>
        </div>

        {/* Section 1 */}
        <section id="kabul-sartlari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
            Pejirandina Mercan û Xalên Giştî
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Ev malper ji bo agahdarkirin, hişyariya civakî, parastina mafan û weşangeriya dîjîtal ji aliyê Tevgera Jinên Azad (TJA) ve tê birêvebirin. Hemû mêvanên ku sûdê ji xizmetên malperê digirin neçar in van mercan bi cih bînin.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Heke hûn van mercan qebûl nakin, ji kerema xwe bikaranîna malpera me bidawî bikin.
          </p>
        </section>

        {/* Section 2 */}
        <section id="fikri-mulkiyet" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
            Mafên Mulkiyeta Rewşenbîrî û Çapê
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Hemû naverokên li ser malperê (nivîs, rapor, daxuyaniyên çapemeniyê, gotar, grafîk, amblem, wêne, deng/vîdyo û hêmanên sêwiranê) aîdî TJA'yê ne û di bin parastina qanûnên neteweyî û navneteweyî yên mafê çapê de ne.
          </p>
          <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-3">
            <h4 className="font-bold text-brand-purple-900 text-sm">Mercên Bikaranîna Naverokê:</h4>
            <ul className="space-y-2 text-sm text-gray-600 font-light list-disc list-inside">
              <li>Rapor û nivîsên li ser malperê bi nîşandana çavkaniyê û girêdanê dikarin ji bo mebestên ne-bazirganî werin vegotin.</li>
              <li>Bikaranîna tevahiya naverokên malperê bêyî destûr ji bo berhem an weşanên bazirganî bi tundî qedexe ye.</li>
              <li>Amblem û hêmanên grafîkî yên orîjînal bêyî destûra nivîskî ya TJA'yê nayên kopîkirin an guhertin.</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section id="kullanim-kurallari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
            Bikaranîna Qebûlkirî û Qedexe
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Dema ku hûn malpera me bi kar tînin, divê hûn li gorî rêgez û zagonan tevbigerin. Karên qedexekirî ev in:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Scraping û Kişandina Daneyan a Otomatîk
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Gihîştina databasa malperê bi rêya bot, spider an amûrên otomatîk ji bo kişandina komî ya daneyan (scraping) qedexe ye.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Êrîşên Sîber û Kodên Zerardar
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Barkirina vîrus, trojan û nermalavên zerardar, an jî êrîşên DDoS ên ku barê zêde dixin ser serveran qedexe ye.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Hewldanên Gihîştina Bêdestûr
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Hewldana gihîştina panela rêveberiyê, databasê an binesaziya serverê sûc e.
              </p>
            </div>
            <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
              <h4 className="font-bold text-red-900 text-sm flex items-center">
                <span className="mr-2">🚫</span> Bikaranîna Formê ya Xapînok
              </h4>
              <p className="text-xs text-gray-600 font-light leading-relaxed">
                Şandina agahiyên sexte, xapînok an daneyên kesane yên aîdî kesên din di formên têkiliyê de qedexe ye.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="dis-baglantilar" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
            Girêdanên Aliyên Sêyem û Daxuyaniya Berpirsiyariyê
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Malpera me dibe ku ji bo agahdarkirinê girêdanên (lînkên) malperên derveyî bihewîne. Li ser naverok, polîtîkayên nepeniyê an pêkanînên wan malperan ti kontrol û berpirsiyariya TJA'yê nîne.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Dema hûn li girêdanên derve bitikînin, hûn dikevin bin mercên bikaranîna wan malperan.
          </p>
        </section>

        {/* Section 5 */}
        <section id="hizmet-degisiklikleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
            Qutbûna Xizmetê û Sînorê Berpirsiyariyê
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            TJA garantî nake ku malper bê qutbûn an bê şaşî bixebite. Ji ber qutbûnên demkî yên ji ber tamîrkirin, nûvekirin an têkçûna serverê TJA berpirsiyar nîne.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Naverokên nûçe û raporên li ser malperê ji bo agahdarkirina giştî ne û şûna şêwirmendiya hiqûqî ya pispor nagirin.
          </p>
        </section>

        {/* Section 6 */}
        <section id="degisiklik-hakki" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">06.</span>
            Mafê Guhertin û Nûvekirina Mercan
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            TJA mafê xwe diparêze ku her dem bêyî agahdarkirina pêşwext van Mercên Bikaranînê biguherîne an nûve bike.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Mercên nûvekirî gava li ser malperê hatin weşandin derbasdar dibin. Berdewamiya bikaranîna malperê tê wateya pejirandina guhertinan.
          </p>
        </section>

        {/* Section 7 */}
        <section id="uygulanacak-hukuk" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">07.</span>
            Qanûna Bicîhbûyî û Têkilî
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Ev Mercên Bikaranînê girêdayî zagonên Komara Tirkiyeyê ne. Dadgehên peywendîdar di nakokiyan de xwedî desthilatdarî ne.
          </p>
          <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-brand-purple-900 text-sm">Ji Bo Pirsên We:</h4>
              <p className="text-xs text-gray-500 font-light">Ji bo pirsên derbarê mercên bikaranînê de bi me re têkilî daynin.</p>
            </div>
            <a
              href="mailto:tja.bilgi@gmail.com"
              className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
            >
              tja.bilgi@gmail.com
            </a>
          </div>
        </section>
      </LegalLayout>
    );
  }

  const sections: TocSection[] = [
    { id: 'kabul-sartlari', title: '1. Şartların Kabulü ve Genel Hükümler' },
    { id: 'fikri-mulkiyet', title: '2. Fikri Mülkiyet ve Telif Hakları' },
    { id: 'kullanim-kurallari', title: '3. Kabul Edilebilir Kullanım ve Yasaklar' },
    { id: 'dis-baglantilar', title: '4. Üçüncü Taraf Bağlantılar ve Sorumluluk' },
    { id: 'hizmet-degisiklikleri', title: '5. Hizmet Kesintileri ve Sorumluluk Sınırı' },
    { id: 'degisiklik-hakki', title: '6. Şartların Güncellenme Hakkı' },
    { id: 'uygulanacak-hukuk', title: '7. Uygulanacak Hukuk ve İletişim' },
  ];

  return (
    <LegalLayout
      title="Kullanım Şartları"
      subtitle="Web Sitesi Kullanım ve Hizmet Koşulları"
      lastUpdated="01 Eylül 2026"
      activeTab="terms"
      sections={sections}
    >
      {/* Intro Note */}
      <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
        <div className="flex items-center space-x-3 font-bold text-base">
          <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
            §
          </span>
          <span>Genel Sözleşme Şartları</span>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed font-light">
          Bu web sitesine (tevgerajinenazad.com) erişerek ve kullanarak, aşağıda belirtilen Kullanım Şartları’nı tamamen okuduğunuzu, anladığınızı ve herhangi bir sınırlama olmaksızın kabul ettiğinizi beyan etmiş olursunuz.
        </p>
      </div>

      {/* Section 1 */}
      <section id="kabul-sartlari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
          Şartların Kabulü ve Genel Hükümler
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Bu site, Tevgera Jinên Azad (TJA) tarafından bilgilendirme, kamuoyu oluşturma, hak savunuculuğu ve dijital yayıncılık amacıyla işletilmektedir. Sitede sunulan hizmetlerden yararlanan tüm ziyaretçiler işbu şartlara uymakla yükümlüdür.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          Bu şartları kabul etmiyorsanız, lütfen sitemizi kullanmaya devam etmeyiniz.
        </p>
      </section>

      {/* Section 2 */}
      <section id="fikri-mulkiyet" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
          Fikri Mülkiyet ve Telif Hakları
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Sitede yer alan tüm içerikler (yazılar, raporlar, basın bültenleri, makaleler, grafikler, amblemler, fotoğraflar, ses/video dosyaları ve tasarım ögeleri) TJA’ya aittir ve ulusal ile uluslararası telif hakkı mevzuatı uyarınca korunmaktadır.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-3">
          <h4 className="font-bold text-brand-purple-900 text-sm">İçerik Kullanım Koşulları:</h4>
          <ul className="space-y-2 text-sm text-gray-600 font-light list-disc list-inside">
            <li>Sitedeki rapor ve metinler kaynak gösterilerek ve bağlantı (link) verilerek ticari olmayan amaçlarla alıntılanabilir.</li>
            <li>Sitedeki içeriklerin tamamının izinsiz olarak ticari ürün veya yayın haline getirilmesi kesinlikle yasaktır.</li>
            <li>Logolar ve özgün grafik ögeleri TJA’nın yazılı izni olmadan kopyalanamaz veya değiştirilemez.</li>
          </ul>
        </div>
      </section>

      {/* Section 3 */}
      <section id="kullanim-kurallari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
          Kabul Edilebilir Kullanım ve Yasaklar
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Sitemizi kullanırken aşağıdaki kurallara ve yasalara uygun hareket etmeniz gerekmektedir. Yasaklanan eylemler şunlardır:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
            <h4 className="font-bold text-red-900 text-sm flex items-center">
              <span className="mr-2">🚫</span> Otomatik Scraping & Veri Çekme
            </h4>
            <p className="text-xs text-gray-600 font-light leading-relaxed">
              Botlar, örümcekler (spiders) veya otomatik araçlar vasıtasıyla sitenin veri tabanına erişmek, toplu veri çekmek (scraping) yasaktır.
            </p>
          </div>
          <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
            <h4 className="font-bold text-red-900 text-sm flex items-center">
              <span className="mr-2">🚫</span> Siber Saldırı ve Zararlı Kod
            </h4>
            <p className="text-xs text-gray-600 font-light leading-relaxed">
              Siteye virüs, trojan, solucan gibi zararlı kodlar yüklemek veya sunuculara aşırı yük bindirecek DDoS saldırılarında bulunmak yasaktır.
            </p>
          </div>
          <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
            <h4 className="font-bold text-red-900 text-sm flex items-center">
              <span className="mr-2">🚫</span> Yetkisiz Erişim Denemeleri
            </h4>
            <p className="text-xs text-gray-600 font-light leading-relaxed">
              Yönetim paneline, veritabanına veya sunucu altyapısına yetkisiz erişim sağlamaya çalışmak suç teşkil eder.
            </p>
          </div>
          <div className="p-5 bg-red-50/50 rounded-2xl border border-red-100 space-y-2">
            <h4 className="font-bold text-red-900 text-sm flex items-center">
              <span className="mr-2">🚫</span> Yanıltıcı Form Kullanımı
            </h4>
            <p className="text-xs text-gray-600 font-light leading-relaxed">
              İletişim formlarına sahte, yanıltıcı veya başkasının kimliğine ait kişisel veriler girmek yasaktır.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section id="dis-baglantilar" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
          Üçüncü Taraf Bağlantılar ve Sorumluluk Reddi
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Sitemiz, bilgilendirme amacıyla üçüncü taraf web sitelerine bağlantılar (linkler) içerebilir. Bu harici sitelerin içeriği, gizlilik politikaları veya uygulamaları üzerinde TJA’nın hiçbir denetimi ve sorumluluğu bulunmamaktadır.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          Harici bağlantılara tıklamanız halinde ilgili üçüncü taraf sitenin kullanım şartlarına tabi olursunuz.
        </p>
      </section>

      {/* Section 5 */}
      <section id="hizmet-degisiklikleri" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
          Hizmet Kesintileri ve Sorumluluk Sınırı
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          TJA, web sitemizin kesintisiz veya hatasız çalışacağını garanti etmez. Bakım, güncelleme veya sunucu arızaları nedeniyle oluşabilecek geçici kesintilerden dolayı TJA sorumlu tutulamaz.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          Sitede yer alan haber ve rapor içerikleri genel bilgilendirme niteliğinde olup, hukuki tavsiye yerine geçmez.
        </p>
      </section>

      {/* Section 6 */}
      <section id="degisiklik-hakki" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">06.</span>
          Şartların Güncellenme Hakkı
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          TJA, dilediği zaman işbu Kullanım Şartları’nı önceden bildirmeksizin tek taraflı olarak değiştirme veya güncelleme hakkını saklı tutar.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          Güncellenmiş şartlar sitede yayınlandığı andan itibaren geçerlilik kazanır. Sitemizi kullanmaya devam etmeniz, değişiklikleri kabul ettiğiniz anlamına gelir.
        </p>
      </section>

      {/* Section 7 */}
      <section id="uygulanacak-hukuk" className="scroll-mt-32 space-y-4">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">07.</span>
          Uygulanacak Hukuk ve İletişim
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          İşbu Kullanım Şartları Türkiye Cumhuriyeti kanunlarına tabidir. Şartların uygulanmasından doğabilecek uyuşmazlıklarda yasal merciler yetkilidir.
        </p>
        <div className="bg-brand-purple-50 p-6 rounded-2xl border border-brand-purple-100 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-brand-purple-900 text-sm">Sorularınız İçin:</h4>
            <p className="text-xs text-gray-500 font-light">Kullanım şartları hakkındaki sorularınız için bize ulaşın.</p>
          </div>
          <a
            href="mailto:tja.bilgi@gmail.com"
            className="px-5 py-2.5 bg-brand-purple-900 text-white rounded-xl font-bold text-xs hover:bg-brand-purple-700 transition-colors"
          >
            tja.bilgi@gmail.com
          </a>
        </div>
      </section>
    </LegalLayout>
  );
};

export default TermsOfUse;
